import { c as _c } from "react/compiler-runtime";
import { IcoArrowUpRight16, IcoKey16, IcoNetwork16, IcoUserCircle16 } from '@onefootprint/icons';
import { getErrorMessage } from '@onefootprint/request';
import type { ApiError, AuthMethodKind, GraphOperationUpdateSettings, PlaybookSettings } from '@onefootprint/request-types/dashboard';
import { Form, InlineAlert, LinkButton } from '@onefootprint/ui';
import Link from 'next/link';
import { type ReactNode, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useOrgSession from '@/hooks/use-org-session';
import { TenantFeatureGate } from '@/types/tenant';
import useCanModernize from '../../../../../use-can-modernize';
import type { FormData } from '../../../playbook-settings.types';
import SettingsModernize from '../settings-modernize';
export type SettingsDefaultFormProps = {
  playbook: PlaybookSettings;
  onSave?: (data: Omit<GraphOperationUpdateSettings, 'kind'>) => Promise<void>;
  saveError: ApiError | null;
  setIsFormDirty: (isDirty: boolean) => void;
  modernize?: ReactNode;
};
const SettingsDefaultForm = t0 => {
  const $ = _c(55);
  const {
    playbook,
    onSave,
    setIsFormDirty,
    saveError,
    modernize
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "settingsDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const featureGates = useOrgSession().data?.featureGates ?? [];
  const hasSmsOtp = featureGates.includes(TenantFeatureGate.SmsOtp);
  const hasEmailOtp = featureGates.includes(TenantFeatureGate.EmailOtp);
  const hasAnyOtp = hasSmsOtp || hasEmailOtp;
  const canModernize = useCanModernize();
  const t2 = !onSave;
  const t3 = playbook.isModern;
  const t4 = playbook.requiresAuthentication;
  let t5;
  if ($[1] !== playbook.requiredAuthMethods) {
    t5 = playbook.requiredAuthMethods.includes("phone");
    $[1] = playbook.requiredAuthMethods;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== playbook.requiredAuthMethods) {
    t6 = playbook.requiredAuthMethods.includes("email");
    $[3] = playbook.requiredAuthMethods;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = {
      phone: t5,
      email: t6
    };
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = playbook.promptForPasskey;
  const t9 = Boolean(playbook.supportInternalMatching);
  let t10;
  if ($[8] !== playbook.isModern || $[9] !== playbook.promptForPasskey || $[10] !== playbook.requiresAuthentication || $[11] !== t7 || $[12] !== t9) {
    t10 = {
      isModern: t3,
      requiresAuthentication: t4,
      requiredAuthMethods: t7,
      promptForPasskey: t8,
      supportInternalMatching: t9
    };
    $[8] = playbook.isModern;
    $[9] = playbook.promptForPasskey;
    $[10] = playbook.requiresAuthentication;
    $[11] = t7;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t2) {
    t11 = {
      disabled: t2,
      defaultValues: t10
    };
    $[14] = t10;
    $[15] = t2;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const formMethods = useForm(t11);
  const {
    control,
    handleSubmit,
    formState,
    setValue
  } = formMethods;
  let t12;
  if ($[17] !== control) {
    t12 = {
      control,
      name: "isModern"
    };
    $[17] = control;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const isModern = useWatch(t12);
  let t13;
  if ($[19] !== control) {
    t13 = {
      control,
      name: "requiresAuthentication"
    };
    $[19] = control;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const requiresAuthentication = useWatch(t13);
  let t14;
  if ($[21] !== control) {
    t14 = {
      control,
      name: "supportInternalMatching"
    };
    $[21] = control;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const supportInternalMatching = useWatch(t14);
  let t15;
  if ($[23] !== formState.dirtyFields || $[24] !== setIsFormDirty) {
    t15 = () => {
      setIsFormDirty(Object.keys(formState.dirtyFields).length > 0);
    };
    $[23] = formState.dirtyFields;
    $[24] = setIsFormDirty;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== formState || $[27] !== setIsFormDirty) {
    t16 = [formState, setIsFormDirty];
    $[26] = formState;
    $[27] = setIsFormDirty;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  useEffect(t15, t16);
  const handleFormSubmit = async data => {
    if (!onSave) {
      console.error("No onSave function provided in settings-default-form");
      return;
    }
    const getRequiredAuthMethods = () => {
      const methods = [];
      if (!data.requiresAuthentication) {
        return methods;
      }
      if (data.requiredAuthMethods.phone && hasSmsOtp) {
        methods.push("phone");
      }
      if (data.requiredAuthMethods.email && hasEmailOtp) {
        methods.push("email");
      }
      return methods;
    };
    const isModern_0 = canModernize ? data.isModern : undefined;
    if (isModern_0) {
      onSave({
        isModern: isModern_0,
        requiresAuthentication: false,
        requiredAuthMethods: [],
        promptForPasskey: false,
        supportInternalMatching: false
      });
      return;
    }
    onSave({
      isModern: isModern_0,
      requiresAuthentication: hasAnyOtp && data.requiresAuthentication,
      requiredAuthMethods: getRequiredAuthMethods(),
      promptForPasskey: data.promptForPasskey,
      supportInternalMatching: hasAnyOtp && data.supportInternalMatching
    });
  };
  const T0 = FormProvider;
  const t17 = handleSubmit(handleFormSubmit);
  let t18;
  if ($[29] !== t) {
    t18 = t(_temp);
    $[29] = t;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== saveError) {
    t19 = saveError && <InlineAlert variant="error" aria-label="error-message">{getErrorMessage(saveError)}</InlineAlert>;
    $[31] = saveError;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== hasAnyOtp || $[34] !== hasEmailOtp || $[35] !== hasSmsOtp || $[36] !== isModern || $[37] !== requiresAuthentication || $[38] !== setValue || $[39] !== supportInternalMatching || $[40] !== t) {
    t20 = !isModern && <>{hasAnyOtp && <div className="flex flex-col gap-4"><div className="flex flex-col gap-1"><div className="flex gap-2 items-center"><IcoNetwork16 /><div className="text-label-2 text-primary">{t(_temp2)}</div></div><div className="text-body-2 text-secondary">{t(_temp3)}</div></div><div className="flex flex-col gap-4"><Form.Toggle name="requiresAuthentication" disabled={false} size="compact" label={t(_temp4)} onChange={checked => {
            setValue("requiresAuthentication", checked);
            if (!checked) {
              setValue("requiredAuthMethods.phone", false);
              setValue("requiredAuthMethods.email", false);
            }
          }} />{requiresAuthentication ? <div className="flex flex-col gap-2">{hasSmsOtp && <Form.Checkbox label={t(_temp5)} name="requiredAuthMethods.phone" />}{hasEmailOtp && <Form.Checkbox label={t(_temp6)} name="requiredAuthMethods.email" />}</div> : <InlineAlert variant="neutral" aria-label="no-auth-warn"><Trans ns="playbook-builder" i18nKey={_temp7} components={{
              onboardingSessionLink: <Link href="https://docs.onefootprint.com/articles/integrate/onboarding-sessions#example-use-cases-guarantee-resolution-to-a-specific-user" target="_blank" className="text-label-2 text-accent hover:text-accent-hover">{t(_temp8)}</Link>,
              fpId: <code className="align-baseline">{t(_temp9)}</code>,
              userExternalId: <code className="align-baseline">{t(_temp10)}</code>
            }} /></InlineAlert>}</div></div>}<div className="flex flex-col gap-4"><div className="flex flex-col gap-1"><div className="flex gap-2 items-center"><IcoKey16 /><div className="text-label-2 text-primary">{t(_temp11)}</div></div><div className="text-body-2 text-secondary">{t(_temp12)}</div></div><div className="flex flex-col gap-1"><Form.Toggle name="promptForPasskey" size="compact" label={<span className="text-label-2">{t(_temp13)}</span>} /><div className="flex flex-col gap-1 pl-11 text-body-2 text-tertiary"><p>{t(_temp14)}</p><p>{t(_temp15)}</p><div className="flex flex-wrap items-center gap-x-2 gap-y-1"><LinkButton href="https://developer.apple.com/passkeys/" target="_blank" variant="label-2" iconComponent={IcoArrowUpRight16}>{t(_temp16)}</LinkButton><LinkButton href="https://developer.android.com/design/ui/mobile/guides/patterns/passkeys" target="_blank" variant="label-2" iconComponent={IcoArrowUpRight16}>{t(_temp17)}</LinkButton></div></div></div></div>{hasAnyOtp && <div className="flex flex-col gap-4"><div className="flex flex-col gap-1"><div className="flex gap-1 items-center"><IcoUserCircle16 /><div className="text-label-2 text-primary">{t(_temp18)}</div></div><div className="text-body-2 text-secondary">{t(_temp19)}</div></div><div className="flex flex-col gap-4"><Form.Toggle name="supportInternalMatching" label={t(_temp20)} size="compact" />{supportInternalMatching && <InlineAlert variant="neutral" aria-label="internal-matching-warn"><Trans ns="playbook-builder" i18nKey={_temp21} components={{
              fpId: <code className="align-baseline">{t(_temp22)}</code>,
              userExternalId: <code className="align-baseline">{t(_temp23)}</code>
            }} /></InlineAlert>}</div></div>}</>;
    $[33] = hasAnyOtp;
    $[34] = hasEmailOtp;
    $[35] = hasSmsOtp;
    $[36] = isModern;
    $[37] = requiresAuthentication;
    $[38] = setValue;
    $[39] = supportInternalMatching;
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== canModernize || $[43] !== modernize) {
    t21 = canModernize && <SettingsModernize>{modernize}</SettingsModernize>;
    $[42] = canModernize;
    $[43] = modernize;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t17 || $[46] !== t18 || $[47] !== t19 || $[48] !== t20 || $[49] !== t21) {
    t22 = <form onSubmit={t17} id="settings-form" className="flex flex-col gap-8" aria-label={t18}>{t19}{t20}{t21}</form>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== T0 || $[52] !== formMethods || $[53] !== t22) {
    t23 = <T0 {...formMethods}>{t22}</T0>;
    $[51] = T0;
    $[52] = formMethods;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  return t23;
};
export default SettingsDefaultForm;
function _temp($) {
  return $.defaultForm.ariaLabel;
}
function _temp2($_0) {
  return $_0.otp.title;
}
function _temp3($_1) {
  return $_1.otp.description;
}
function _temp4($_2) {
  return $_2.otp.form.enable.label;
}
function _temp5($_3) {
  return $_3.otp.phone;
}
function _temp6($_4) {
  return $_4.otp.email;
}
function _temp7($_5) {
  return $_5.settingsDialog.otp.noAuthenticationInfo;
}
function _temp8($_6) {
  return $_6.otp.onboardingSession;
}
function _temp9($_7) {
  return $_7.otp.fpId;
}
function _temp10($_8) {
  return $_8.otp.userExternalId;
}
function _temp11($_9) {
  return $_9.passkeys.label;
}
function _temp12($_10) {
  return $_10.passkeys.subtitle;
}
function _temp13($_11) {
  return $_11.passkeys.enablePasskeyRegistration;
}
function _temp14($_12) {
  return $_12.passkeys.description;
}
function _temp15($_13) {
  return $_13.passkeys.about.title;
}
function _temp16($_14) {
  return $_14.passkeys.about.ios;
}
function _temp17($_15) {
  return $_15.passkeys.about.android;
}
function _temp18($_16) {
  return $_16.supportInternalMatching.title;
}
function _temp19($_17) {
  return $_17.supportInternalMatching.description;
}
function _temp20($_18) {
  return $_18.supportInternalMatching.title;
}
function _temp21($_19) {
  return $_19.settingsDialog.supportInternalMatching.note;
}
function _temp22($_20) {
  return $_20.otp.fpId;
}
function _temp23($_21) {
  return $_21.otp.userExternalId;
}